import { createColumnHelper } from "@tanstack/react-table";
import clsx from "clsx";
import { Link, RefreshCcw } from "lucide-react";
import moment from "moment";
import { useMemo } from "react";
import { ReactComponent as DotsIcon } from "../../../assets/icons/dots.svg";
import { ReactComponent as InfoIcon } from "../../../assets/icons/info.svg";
import Button from "../../../components/Button";
import { Checkbox } from "../../../components/Checkbox";
import UIPopover from "../../../components/Popover";
import {
  ClientBilling,
  OVERDUE_STATUS_COLOR,
  OVERDUE_STATUS_LABEL,
  STATUS_COLORS,
  STATUS_LABELS,
  isInvoiceOverdue,
} from "../types";
import {
  describeBillingPlan,
  formatCurrency,
  formatCurrencyPrecise,
  getEffectiveStatus,
  isChargeableWithoutCommission,
  rewriteHotelNames,
} from "../utils";
import { HoverTip } from "./HoverTip";
import { RateCell } from "./RateCell";

const columnHelper = createColumnHelper<ClientBilling>();

export type BillingColumnDeps = {
  selectedIds: Set<string>;
  filteredRows: ClientBilling[];
  selectedMonth: string;
  toggleSelect: (id: string) => void;
  toggleSelectAll: () => void;
  knownHotelNames: string[];
  plansLoaded: boolean; // Boolean(allStripePlans)
  loadingPreviewForClientId: string | null;
  isFailedActionPending: boolean; // retry.isPending || charge.isPending || isLoadingPreview
  retryingChargeId: string | null; // retryChargeMutation.variables?.chargeId when pending
  onChargeCard: (client: ClientBilling) => void;
  onRetry: (client: ClientBilling) => void;
  onRecharge: (client: ClientBilling) => void;
  onSyncPaymentMethod: (client: ClientBilling) => void;
  onLinkStripe: (client: ClientBilling) => void;
  onCopyPayLink: (client: ClientBilling) => void;
  onSendInvoiceEmail: (client: ClientBilling) => void;
  onVoidInvoice: (client: ClientBilling) => void;
  onToggleTestUser: (client: ClientBilling) => void;
};

export function useBillingColumns(d: BillingColumnDeps) {
  return useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: () => (
          <Checkbox
            id="select-all"
            name="select-all"
            checked={
              d.selectedIds.size > 0 &&
              d.selectedIds.size === d.filteredRows.length
            }
            onChange={d.toggleSelectAll}
          />
        ),
        cell: (info) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              id={`select-${info.row.original.id}`}
              name={`select-${info.row.original.id}`}
              checked={d.selectedIds.has(info.row.original.id)}
              onChange={() => d.toggleSelect(info.row.original.id)}
            />
          </div>
        ),
      }),
      columnHelper.accessor("clientName", {
        header: "Client",
        cell: (info) => {
          const client = info.row.original;
          const displayName =
            client.clientName || client.hotelName || client.email;
          return (
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-content-primary">
                  {displayName}
                </span>
                {client.isTestUser && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-300 leading-none">
                    TEST
                  </span>
                )}
              </div>
              <span className="text-xs text-content-tertiary">
                {client.email}
              </span>
            </div>
          );
        },
      }),
      // Sorts on what is displayed: our own billing config first, Stripe second.
      columnHelper.accessor(
        (row) => describeBillingPlan(row) ?? row.planDescription ?? "",
        {
          id: "planDescription",
          header: "Plan",
          cell: (info) => {
            const client = info.row.original;
            // What we bill, from client_billing_config. Resolves instantly and
            // covers the arrangements Stripe has no plan object for at all —
            // commission-only, and the performance-backed minimum.
            const configured = describeBillingPlan(client);
            // The Stripe subscription, if the client carries one. Kept as the
            // second line so legacy fixed-plan rows still show their product.
            const stripePlan = client.planDescription
              ? rewriteHotelNames(
                  client.planDescription,
                  client.hotelName,
                  d.knownHotelNames,
                )
              : null;

            // Only a row with nothing configured is still waiting on Stripe.
            if (!configured && !stripePlan) {
              return d.plansLoaded ? (
                <span className="text-sm text-gray-400">No plan</span>
              ) : (
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
              );
            }

            const primary = configured ?? (stripePlan as string);
            const secondary = configured ? stripePlan : null;
            const tooltip = secondary ? `${primary} — ${secondary}` : primary;
            return (
              <div className="relative group max-w-[220px] cursor-default">
                <span className="text-sm text-content-primary block truncate">
                  {primary}
                </span>
                {secondary && (
                  <span className="text-xs text-content-tertiary block truncate">
                    {secondary}
                  </span>
                )}
                {tooltip.length > 30 && (
                  <div className="absolute z-[9999] hidden group-hover:block top-full left-0 mt-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg max-w-[320px] whitespace-normal">
                    {tooltip}
                    <div className="absolute bottom-full left-4 border-4 border-transparent border-b-gray-900"></div>
                  </div>
                )}
              </div>
            );
          },
        },
      ),
      columnHelper.display({
        id: "billingInfo",
        header: "Hotel Name",
        cell: (info) => {
          const client = info.row.original;
          const address = client.billingAddress;
          const hotelName = client.hotelName;
          return (
            <div className="flex flex-col gap-0.5">
              <span className="text-sm text-content-primary">
                {hotelName || client.billingName || "—"}
              </span>
              {address && (
                <span className="text-xs text-content-tertiary">
                  {address.city}, {address.state}
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor("accountCreatedAt", {
        header: "Account Created",
        cell: (info) => {
          const createdAt = info.getValue();
          if (!createdAt) {
            return <span className="text-sm text-gray-400">—</span>;
          }
          return (
            <span className="text-sm text-content-primary">
              {moment(createdAt).format("MMM D, YYYY")}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: "paymentMethod",
        header: "Payment Method",
        cell: (info) => {
          const client = info.row.original;
          if (client.enrichmentPending) {
            return (
              <div className="flex flex-col gap-1">
                <span className="inline-block w-20 h-4 bg-gray-100 animate-pulse rounded" />
                <span className="inline-block w-14 h-3 bg-gray-100 animate-pulse rounded" />
              </div>
            );
          }
          const pm = client.paymentMethod;
          if (!pm) return <span className="text-sm text-gray-400">—</span>;
          return (
            <div className="flex flex-col gap-0.5">
              <span className="text-sm text-content-primary capitalize">
                {pm.brand} •••• {pm.last4}
              </span>
              <span className="text-xs text-content-tertiary">
                Exp {pm.expMonth}/{pm.expYear}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor("commissionRate", {
        header: "Rate",
        cell: (info) => <RateCell client={info.row.original} />,
      }),
      columnHelper.accessor("bookingRevenue", {
        header: "Bookings ($)",
        cell: (info) => {
          const client = info.row.original;

          if (
            client.enrichmentPending &&
            client.bookingRevenue === 0 &&
            client.googleAdsCustomerId
          ) {
            return (
              <span className="inline-block w-16 h-4 bg-gray-100 animate-pulse rounded" />
            );
          }

          return (
            <span className="font-medium text-content-primary">
              {formatCurrency(info.getValue())}
            </span>
          );
        },
      }),
      columnHelper.accessor("commission", {
        header: "Commission",
        cell: (info) => {
          const client = info.row.original;
          if (
            client.enrichmentPending &&
            client.commission === 0 &&
            client.googleAdsCustomerId
          ) {
            return (
              <span className="inline-block w-14 h-4 bg-gray-100 animate-pulse rounded" />
            );
          }
          return (
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold text-green-700">
                {formatCurrencyPrecise(info.getValue())}
              </span>
              {client.isManualOverride && (
                <span
                  className="text-[10px] font-medium text-amber-700 bg-amber-100 border border-amber-300 rounded px-1 py-0.5 w-fit"
                  title={
                    client.overrideReason ||
                    "Charged amount was manually overridden"
                  }
                >
                  Manual override
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "addons",
        header: "Add-ons",
        cell: (info) => {
          const enabledAddons = (info.row.original.addons ?? []).filter(
            (a) => a.enabled,
          );
          if (enabledAddons.length === 0) {
            return <span className="text-sm text-gray-400">—</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {enabledAddons.map((addon) => (
                <span
                  key={addon.type}
                  className="inline-flex items-center text-[0.6875rem] font-medium px-1.5 py-0.5 rounded bg-[#3d828f]/10 text-primary border border-[#3d828f]/20 leading-none whitespace-nowrap"
                  title={
                    addon.monthlyPrice != null
                      ? `$${addon.monthlyPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo`
                      : undefined
                  }
                >
                  {addon.displayName}
                </span>
              ))}
            </div>
          );
        },
      }),
      columnHelper.accessor("invoiceStatus", {
        header: "Status",
        cell: (info) => {
          const client = info.row.original;

          const effectiveStatus = getEffectiveStatus(client);

          const overdue = isInvoiceOverdue(client);

          // Generate tooltip based on status and what's missing
          let tooltip = "";
          if (effectiveStatus === "not_configured") {
            tooltip = "Missing: commission rate";
          } else if (effectiveStatus === "pending") {
            if (
              client.commissionRate === 0 &&
              !isChargeableWithoutCommission(client)
            ) {
              tooltip = "Commission rate is set to 0%";
            } else if (!client.canCharge) {
              const missing: string[] = [];
              if (!client.stripeCustomerId) missing.push("Stripe customer");
              if (!client.paymentMethod) missing.push("payment method");
              tooltip =
                missing.length > 0
                  ? `Missing: ${missing.join(", ")}`
                  : client.warningMessage || "Cannot charge yet";
            } else {
              tooltip = "Ready to charge";
            }
          } else if (effectiveStatus === "invoiced") {
            const sentPart =
              client.invoicedAt && moment(client.invoicedAt).isValid()
                ? `Invoice sent ${moment(client.invoicedAt).format("MMM D")}`
                : "Invoice sent";
            const duePart =
              client.invoiceDueAt && moment(client.invoiceDueAt).isValid()
                ? ` — ${overdue ? "was due" : "due"} ${moment(
                    client.invoiceDueAt,
                  ).format("MMM D, YYYY")}`
                : "";
            tooltip = `${sentPart}${duePart}; awaiting customer payment`;
          } else if (effectiveStatus === "paid") {
            tooltip = "Payment completed";
          } else if (effectiveStatus === "failed") {
            tooltip = client.warningMessage || "Payment failed";
          }

          const needsInfoIcon =
            (effectiveStatus === "pending" ||
              effectiveStatus === "not_configured") &&
            Boolean(tooltip);

          return (
            <div className="flex items-center gap-1.5">
              <div className={`relative ${needsInfoIcon ? "" : "group"}`}>
                <div
                  className={`${
                    overdue
                      ? OVERDUE_STATUS_COLOR
                      : STATUS_COLORS[effectiveStatus]
                  } text-xs whitespace-nowrap font-medium border rounded py-1 px-2 flex items-center justify-center w-fit  ${
                    needsInfoIcon ? "" : "cursor-help"
                  }`}
                >
                  {overdue
                    ? OVERDUE_STATUS_LABEL
                    : STATUS_LABELS[effectiveStatus]}
                </div>
                {tooltip &&
                  effectiveStatus !== "pending" &&
                  effectiveStatus !== "not_configured" && (
                    <div className="absolute z-50 hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg whitespace-nowrap">
                      {tooltip}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  )}
              </div>
              {needsInfoIcon && (
                <div className="relative group/status shrink-0">
                  <InfoIcon
                    className="w-3.5 h-3.5 text-content-tertiary cursor-help"
                    aria-label="Status reason"
                  />
                  <div className="absolute z-50 hidden group-hover/status:block bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg whitespace-nowrap">
                    {tooltip}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const client = info.row.original;
          const showChargeButton =
            client.invoiceStatus === "pending" ||
            client.invoiceStatus === "not_configured";
          const showRetryButton =
            client.invoiceStatus === "failed" &&
            client.collectionMethod !== "send_invoice";
          // A failed ATTEMPT to issue an invoice has no card payment to retry —
          // the admin re-opens the billing dialog and re-issues instead.
          const showFailedInvoiceActions =
            client.invoiceStatus === "failed" &&
            client.collectionMethod === "send_invoice";
          const showInvoicedActions = client.invoiceStatus === "invoiced";
          // A $0-commission client is still chargeable when an add-on or platform
          // fee bills on its own — only block when there's genuinely nothing to bill.
          const chargeableWithoutCommission =
            isChargeableWithoutCommission(client);
          // canCharge=false means the AUTO-charge path is blocked (usually a
          // missing card) — but a linked Stripe customer can still be billed by
          // emailed invoice, so only a missing customer blocks the dialog.
          const hasBillingTarget =
            client.canCharge !== false || Boolean(client.stripeCustomerId);
          const isChargeDisabled =
            !hasBillingTarget ||
            (!chargeableWithoutCommission &&
              (client.commissionRate === 0 ||
                (client.bookingRevenue === 0 && client.commission === 0)));
          const disabledReason =
            (!hasBillingTarget && client.warningMessage) ||
            (!chargeableWithoutCommission && client.commissionRate === 0
              ? "Commission rate is 0%"
              : "") ||
            (!chargeableWithoutCommission && client.bookingRevenue === 0
              ? "No booking revenue for this period"
              : "");

          return (
            <div
              className="flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              {showRetryButton ? (
                <>
                  <div title="Retry the existing invoice">
                    <Button
                      onClick={() => d.onRetry(client)}
                      className="!py-1.5 !px-3 !text-xs !bg-red-600 hover:!bg-red-700"
                      disabled={d.isFailedActionPending}
                    >
                      {d.retryingChargeId === client.id
                        ? "Retrying..."
                        : "Retry"}
                    </Button>
                  </div>
                  <div title="Edit amount and recharge">
                    <Button
                      variant="outline"
                      onClick={() => d.onChargeCard(client)}
                      className="!py-1.5 !px-3 !text-xs"
                      disabled={d.isFailedActionPending}
                    >
                      {d.loadingPreviewForClientId === client.id
                        ? "Loading..."
                        : "Adjust"}
                    </Button>
                  </div>
                </>
              ) : showFailedInvoiceActions ? (
                <div title="The invoice couldn't be issued — review and re-send it">
                  <Button
                    onClick={() => d.onChargeCard(client)}
                    className="!py-1.5 !px-3 !text-xs !bg-red-600 hover:!bg-red-700"
                    disabled={d.isFailedActionPending}
                  >
                    {d.loadingPreviewForClientId === client.id
                      ? "Loading..."
                      : "Re-send Invoice"}
                  </Button>
                </div>
              ) : showInvoicedActions ? (
                <>
                  <div title="Open the customer's Stripe payment page">
                    <Button
                      variant="outline"
                      onClick={() =>
                        client.stripeHostedInvoiceUrl &&
                        window.open(
                          client.stripeHostedInvoiceUrl,
                          "_blank",
                          "noopener",
                        )
                      }
                      className="!py-1.5 !px-3 !text-xs"
                      disabled={!client.stripeHostedInvoiceUrl}
                    >
                      View Invoice
                    </Button>
                  </div>
                </>
              ) : showChargeButton ? (
                <>
                  <div title={disabledReason}>
                    <Button
                      onClick={() => d.onChargeCard(client)}
                      className="!py-1.5 !px-3 !text-xs !bg-primary hover:!bg-primary-hover"
                      disabled={isChargeDisabled}
                    >
                      Charge
                    </Button>
                  </div>
                  <HoverTip label="Sync Payment Method">
                    <RefreshCcw
                      className="w-4 h-4 text-content-tertiary hover:text-content-primary cursor-pointer"
                      onClick={() => d.onSyncPaymentMethod(client)}
                      aria-label="Sync Payment Method"
                    />
                  </HoverTip>
                  <HoverTip
                    label={
                      client.stripeCustomerId
                        ? "Change Stripe Customer"
                        : "Link Stripe Customer"
                    }
                  >
                    <Link
                      className="w-4 h-4 text-content-tertiary hover:text-primary cursor-pointer"
                      onClick={() => d.onLinkStripe(client)}
                      aria-label={
                        client.stripeCustomerId
                          ? "Change Stripe Customer"
                          : "Link Stripe Customer"
                      }
                    />
                  </HoverTip>
                </>
              ) : (
                <>
                  <button
                    onClick={() => d.onChargeCard(client)}
                    className="text-xs text-content-tertiary hover:text-content-primary underline"
                  >
                    View Details
                  </button>
                  <div title="Charge this client again (e.g. after a refund)">
                    <Button
                      variant="outline"
                      onClick={() => d.onRecharge(client)}
                      className="!py-1.5 !px-3 !text-xs"
                    >
                      Charge Again
                    </Button>
                  </div>
                </>
              )}
              <UIPopover
                trigger={
                  <button className="!rounded-md h-[2rem] w-[2rem] flex !p-1 items-center justify-center hover:bg-surface-secondary">
                    <DotsIcon className="w-4 h-4" />
                  </button>
                }
              >
                {(close) => (
                  <div className="flex flex-col text-xs text-content-primary font-medium gap-2 bg-surface-primary p-3 rounded-lg shadow-lg min-w-[140px]">
                    {showInvoicedActions && (
                      <>
                        <button
                          className="text-start hover:text-primary"
                          onClick={() => {
                            d.onCopyPayLink(client);
                            close();
                          }}
                          disabled={!client.stripeHostedInvoiceUrl}
                        >
                          Copy Pay Link
                        </button>
                        <button
                          className="text-start hover:text-primary"
                          onClick={() => {
                            d.onSendInvoiceEmail(client);
                            close();
                          }}
                        >
                          Send Invoice Email
                        </button>
                        <button
                          className="text-start hover:text-red-600"
                          onClick={() => {
                            d.onVoidInvoice(client);
                            close();
                          }}
                        >
                          Void Invoice…
                        </button>
                      </>
                    )}
                    <button
                      className={clsx(
                        "text-start",
                        client.isTestUser
                          ? "hover:text-green-600"
                          : "hover:text-orange-600",
                      )}
                      onClick={() => {
                        d.onToggleTestUser(client);
                        close();
                      }}
                    >
                      {client.isTestUser ? "Unmark as Test" : "Mark as Test"}
                    </button>
                  </div>
                )}
              </UIPopover>
            </div>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      d.selectedIds,
      d.filteredRows,
      d.selectedMonth,
      d.knownHotelNames,
      d.plansLoaded,
      d.loadingPreviewForClientId,
      d.isFailedActionPending,
      d.retryingChargeId,
    ],
  );
}
